import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CreditCard, Landmark, Terminal } from "lucide-react";

// Settings → Payment Methods (moved from the Owner Dashboard in Build B). Informational + WiPay entry point only:
// nothing here affects onboarding completion, checkout tender behaviour or saved payment configuration.
export default function PaymentMethodsCard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const modules = useModules();
  const [cfg, setCfg] = useState(null);
  const wipayEnabled = !modules.isExplicitlyOff("wipay");
  useEffect(() => {
    if (!wipayEnabled) { setCfg({}); return; }
    api.get("/wipay/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, [wipayEnabled]);

  const connected = !!(cfg?.connected && cfg?.verified);
  const isOwner = user?.role === "owner";
  const rows = [
    { key: "wipay", name: "WiPay", icon: CreditCard, status: cfg ? (connected ? "Connected" : "Not Connected") : "…",
      cls: connected ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" : "bg-slate-500/10 text-slate-600 border-slate-500/30",
      action: !wipayEnabled ? <span className="text-[11px] text-muted-foreground">Ask NEEC to enable</span>
        : isOwner ? <Button size="sm" variant="outline" className="h-8" onClick={() => nav("/payments-setup")} data-testid="pi-wipay-action">{connected ? "Manage" : "Connect"}</Button>
        : <span className="text-[11px] text-muted-foreground">Owner manages WiPay</span> },
    { key: "scotia", name: "Scotia Card Terminal", icon: Landmark, status: "Coming Soon", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
    { key: "ncb", name: "NCB Card Terminal", icon: Landmark, status: "Coming Soon", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
    { key: "other", name: "Other Bank Terminal", icon: Terminal, status: "Supported manually", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
      note: "Select Card as the tender at checkout and process the payment on your standalone terminal." },
  ];
  return (
    <Card className="p-5" data-testid="payment-integrations">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-head font-bold">Optional Payment Methods</p>
        <span className="text-[11px] text-muted-foreground">Optional · does not affect setup completion</span>
      </div>
      <div className="mt-3 divide-y divide-border">
        {rows.map((r) => { const Icon = r.icon; return (
          <div key={r.key} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`pi-${r.key}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
              <div className="min-w-0"><p className="text-sm font-medium">{r.name}</p>{r.note && <p className="text-[11px] text-muted-foreground">{r.note}</p>}</div>
            </div>
            <div className="flex items-center gap-2"><Badge className={`text-[10px] ${r.cls}`} data-testid={`pi-${r.key}-status`}>{r.status}</Badge>{r.action}</div>
          </div>); })}
      </div>
    </Card>
  );
}
